import { Button, Card, Group, Text } from "@mantine/core";
import type { FC } from "react";
import { GreenCircleCheck } from "@/sales-tools/challenges/icons/GreenCircleCheck";
import { TrainingSessionChallengeResultStatus } from "../types/trainingSession";
import classes from "./TrainingProfileTile.module.css";

export interface TrainingProfileTileProps {
    id: string;
    description: string;
    challengeTaken: boolean;
    challengeStatus?: TrainingSessionChallengeResultStatus;
    onStart?: () => void;
    onViewResults?: () => Promise<void>;
}

export const TrainingProfileTile: FC<TrainingProfileTileProps> = ({
    id,
    description,
    challengeTaken,
    challengeStatus,
    onStart,
    onViewResults,
}) => {
    const challengeComplete = challengeStatus?.toLowerCase() === TrainingSessionChallengeResultStatus.CLEARED;
    // Colors
    const bgColor = challengeComplete ? "var(--mantine-color-dark-6)" : "var(--mantine-color-dark-5)";
    const labelColor = challengeComplete ? "var(--mantine-color-success-8)" : "var(--mantine-color-primary-3)";
    const descriptionColor = challengeComplete ? "var(--mantine-color-dark-3)" : "var(--mantine-color-dark-1)";

    return (
        <Card
            withBorder
            className={classes.tile}
            style={{
                background: bgColor,
                borderColor: "var(--mantine-color-dark-4)",
                borderWidth: 1,
                borderStyle: "solid",
                cursor: "default",
                position: "relative",
                transition: "transform 0.15s cubic-bezier(.4,1.2,.6,1), box-shadow 0.15s, border-color 0.15s",
            }}
            data-testid={`training-profile-tile-${id}`}
            tabIndex={0}
        >
            <Group>
                <Text c={labelColor} tt="capitalize" className={classes.name}>
                    {id.replaceAll("_", " ")}
                </Text>
                {/* Passed badge and checkmark */}
                {challengeComplete && (
                    <Group gap="3px">
                        <svg className={classes.passedIcon}>{GreenCircleCheck}</svg>
                        <Text size="sm" c={"var(--mantine-color-success-6)"}>
                            Passed
                        </Text>
                    </Group>
                )}
            </Group>
            <Text className={classes.description} c={descriptionColor}>
                {description}
            </Text>
            <div className={classes.actionsRow}>
                <Button
                    variant="filled"
                    color="gray"
                    size="sm"
                    onClick={() => void onStart?.()}
                    className={classes.retryButton}
                    data-testid={`retry-button-${id}`}
                >
                    {challengeTaken ? "Retry" : "Start"}
                </Button>
                {challengeTaken && (
                    <button
                        type="button"
                        className={classes.viewResultsLink}
                        onClick={() => void onViewResults?.()}
                        data-testid={`view-results-link-${id}`}
                    >
                        View results
                    </button>
                )}
            </div>
        </Card>
    );
};

export default TrainingProfileTile;
